import React from 'react';

import { CHANNEL_CONFIG_PATH, CHANNEL_PATH, ORDER_OUTPUT_MAPPER_KEY, OVERVIEW_PATH } from '../constants.ts';
import * as DocumentQueries from './queries.tsx';
import { DocumentTypeName, ValidationCheck } from '../../../document/components/DocumentConfig.tsx';
import DocumentXMLSerializerConfiguration from '../../../document/components/XmlSerializerConfig.tsx';
import { DocumentType } from '../../../document/constants.ts';
import DocumentConfiguration, {
    CONFIG_GROUP,
    CONFIG_OPTION,
    DEFAULT_CONFIG_GROUPS,
    DEFAULT_CONFIGS,
    MATCHING_CONFIG,
} from '../../../document/pages/Configuration';
import FormsConfig from '../../../forms/pages/FormsConfig.tsx';
import OrderOutputMapperPanel from '../components/OrderOutputMapperPanel.tsx';
import OrderUploadConfiguration from '../components/OrderUploadConfig.tsx';
import { GET_OVERVIEW_DATA } from '../queries.ts';

const configOptions = {
    [CONFIG_OPTION.General]: DEFAULT_CONFIGS[CONFIG_OPTION.General],
    [CONFIG_OPTION.Document]: DEFAULT_CONFIGS[CONFIG_OPTION.Document],
    [CONFIG_OPTION.Fields]: DEFAULT_CONFIGS[CONFIG_OPTION.Fields],
    [CONFIG_OPTION.FineTuning]: DEFAULT_CONFIGS[CONFIG_OPTION.FineTuning],
    [CONFIG_OPTION.CustomizationParams]: DEFAULT_CONFIGS[CONFIG_OPTION.CustomizationParams],
    [CONFIG_OPTION.XmlSerializer]: ({ channel, handlers }) => (
        <DocumentXMLSerializerConfiguration
            config={channel?.xmlSerializerConfig}
            supportsOutputMapper
            outputMapperKey={ORDER_OUTPUT_MAPPER_KEY}
            channelId={channel?.id}
            useOutputMapping={channel?.useOutputMapping}
            onActivate={() => handlers.onCreate('xml_serializer_config')}
            onSubmit={(formData, channelFields) =>
                handlers.onUpdate({ xmlSerializerConfig: formData, ...channelFields })
            }
        />
    ),
    [CONFIG_OPTION.Email]: DEFAULT_CONFIGS[CONFIG_OPTION.Email],
    [CONFIG_OPTION.MasterData]: DEFAULT_CONFIGS[CONFIG_OPTION.MasterData],
    [CONFIG_OPTION.SFTP]: DEFAULT_CONFIGS[CONFIG_OPTION.SFTP],
    [CONFIG_OPTION.AS2]: DEFAULT_CONFIGS[CONFIG_OPTION.AS2],
    [CONFIG_OPTION.Upload]: ({ channel, handlers }) => (
        <OrderUploadConfiguration
            config={channel?.uploadConfig}
            emailConfig={channel?.emailConfig}
            onActivate={() => handlers.onCreate('upload_config')}
            onSubmit={(formData) => handlers.onUpdate({ uploadConfig: formData })}
        />
    ),
    [CONFIG_OPTION.Discard]: DEFAULT_CONFIGS[CONFIG_OPTION.Discard],
    [CONFIG_OPTION.Form]: ({ channel, channelId }) => <FormsConfig channel={channel} channelId={channelId} />,
};

const configGroups = {
    ...DEFAULT_CONFIG_GROUPS,
    [CONFIG_GROUP.Input]: [...(DEFAULT_CONFIG_GROUPS[CONFIG_GROUP.Input] || []), CONFIG_OPTION.Form],
};

// Output mapper is only visible to support users.
const supportUserConfigs = {
    [CONFIG_OPTION.OutputMapper]: ({ channel }) => <OrderOutputMapperPanel channelId={channel?.id} />,
    [CONFIG_OPTION.Matching]: MATCHING_CONFIG,
};

const supportUserConfigGroups = {
    [CONFIG_GROUP.Output]: [CONFIG_OPTION.OutputMapper],
    [CONFIG_GROUP.Processing]: [CONFIG_OPTION.Matching],
};

const documentConfiguration = {
    service: 'orders',
    documentType: DocumentType.Order,
    documentTypeName: 'order',
    activeTab: 'order',
    documentQueries: DocumentQueries,
    getOverviewData: GET_OVERVIEW_DATA,
    channelPath: CHANNEL_PATH,
    overviewPath: OVERVIEW_PATH,
    configPath: CHANNEL_CONFIG_PATH,
    configOptions,
    configGroups,
    supportUserConfigs,
    supportUserConfigGroups,
    documentTypes: [
        DocumentTypeName.DeliveryNote,
        DocumentTypeName.Invoice,
        DocumentTypeName.Offer,
        DocumentTypeName.Order,
        DocumentTypeName.OrderConfirmation,
        DocumentTypeName.RequestForQuotation,
    ],
    validationChecks: [
        ValidationCheck.TotalAmount,
        ValidationCheck.LineItemSum,
        ValidationCheck.PosNumber,
        ValidationCheck.ForgottenLineItems,
        ValidationCheck.IncompleteLineItems,
        ValidationCheck.MaxQuantity,
        ValidationCheck.MinQuantity,
        ValidationCheck.RemoveBulkOrderSummary,
        ValidationCheck.RepeatedLineItems,
        ValidationCheck.CustomerInternalNote,
        ValidationCheck.ErpLock,
        ValidationCheck.MultipleSalesOrgs,
        ValidationCheck.ThousandSeparatorQuantity,
        ValidationCheck.ArticleDescriptionCheck,
        ValidationCheck.ArticleStatus,
        ValidationCheck.OfferMatching,
        ValidationCheck.HasOpenOfferCheck,
        ValidationCheck.UniqueDocumentNumber,
        ValidationCheck.UnitPriceMatchesGrossPrice,
    ],
};

const Configuration = (props) => {
    return <DocumentConfiguration documentConfiguration={documentConfiguration} {...props} />;
};

export default Configuration;
